<script setup lang="ts">
import { Settings } from '@lucide/vue'

import { RepositoryTimeline, TimelineDraft } from '@types'

import {
  DSButton,
  DSPopover,
  DSPopoverContent,
  DSPopoverTrigger,
  DSSheet,
  DSSheetContent,
  DSSheetDescription,
  DSSheetHeader,
  DSSheetTitle,
  DSSheetTrigger,
} from '@ds'
import PeriodScrollerSettings from './PeriodScrollerSettings.vue'

type Props = {
  timeline: RepositoryTimeline
  /** Desktop gets a popover; mobile/native gets a bottom sheet. */
  useSheet: boolean
  disabled?: boolean
}

type Emits = {
  apply: [draft: TimelineDraft]
}

const { timeline, useSheet, disabled = false } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'DSPeriodScrollerTrigger',
})

const open = defineModel<boolean>('open', { default: false })

const handleApply = (draft: TimelineDraft) => {
  emit('apply', draft)
  open.value = false
}
</script>

<template>
  <DSPopover
    v-if="!useSheet"
    v-model:open="open"
  >
    <DSPopoverTrigger as-child>
      <DSButton
        type="button"
        variant="secondary"
        size="icon-sm"
        class="shrink-0 h-9 w-9 hover:bg-card hover:border-brand hover:text-brand"
        :disabled
        aria-label="Timeline settings"
      >
        <Settings />
      </DSButton>
    </DSPopoverTrigger>
    <DSPopoverContent
      align="end"
      class="p-5 w-82.5"
    >
      <PeriodScrollerSettings
        :timeline
        :disabled
        @apply="handleApply"
        @cancel="open = false"
      />
    </DSPopoverContent>
  </DSPopover>
  <DSSheet
    v-else
    v-model:open="open"
  >
    <DSSheetTrigger as-child>
      <DSButton
        type="button"
        variant="secondary"
        size="icon"
        class="shrink-0"
        :disabled
        aria-label="Timeline settings"
      >
        <Settings />
      </DSButton>
    </DSSheetTrigger>
    <DSSheetContent side="bottom">
      <DSSheetHeader>
        <DSSheetTitle class="sr-only">
          Timeline settings
        </DSSheetTitle>
        <DSSheetDescription class="sr-only">
          Choose how many periods to show, their frequency, and the end date.
        </DSSheetDescription>
      </DSSheetHeader>
      <PeriodScrollerSettings
        :timeline
        :disabled
        @apply="handleApply"
        @cancel="open = false"
      />
    </DSSheetContent>
  </DSSheet>
</template>
